import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import { supabase } from '../supabaseClient';
import Navbar from './Navbar';
import { checkAndUpdateEmployeeData } from './employeeUpdates';
import 'react-datepicker/dist/react-datepicker.css';
import './RegistrarVacaciones.css';

function RegistrarVacaciones() {
    const location = useLocation();
    const mode = location.state?.mode || 'full'; // 'full' or 'half'

    const [email, setEmail] = useState('@wsp.com.mx');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [currentView, setCurrentView] = useState('email'); // 'email' or 'calendar'
    const [employee, setEmployee] = useState(null);
    const [selectedDates, setSelectedDates] = useState([]);
    const [overtimeDays, setOvertimeDays] = useState(null);
    const [confirmationMessage, setConfirmationMessage] = useState('');
    const [registeredVacationDays, setRegisteredVacationDays] = useState([]);
    const [userRole, setUserRole] = useState('');

    // Get the user's role when component mounts
    useEffect(() => {
        getUserRole();
    }, []);

    // Function to get the user's role
    const getUserRole = async () => {
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (user) {
                const { data, error } = await supabase
                    .from('users')
                    .select('role')
                    .eq('wsp_email', user.email)
                    .single();

                if (error) throw error;
                setUserRole(data.role);
            }
        } catch (error) {
            console.error('Error fetching user role:', error.message);
            setError('Error al obtener información del usuario. Por favor, intente nuevamente.');
        }
    };

    const handleEmailChange = (e) => {
        setEmail(e.target.value);
    };

    const handleSubmit = async () => {
        setLoading(true);
        setError('');

        try {
            // First, get the employee data
            const { data: employeeData, error: employeeError } = await supabase
                .from('employees')
                .select('*')
                .eq('wsp_email', email.toLowerCase())
                .single();

            if (employeeError || !employeeData) {
                setError('El correo WSP proporcionado no es valido.\nRevise nuevamente que este bien escrito sin espacios sobrantes o un punto al final.\nSi el problema persiste, contacte al administrador de la página');
                setLoading(false);
                return;
            }

            // Check if the user has permission to manage this employee's vacations
            const hasPermission = checkUserPermission(userRole, employeeData.area);
            
            if (!hasPermission) {
                setError('Usted no esta a cargo de las vacaciones de este empleado.');
                setLoading(false);
                return;
            }

            // Run the employee through the update algorithm
            const result = await checkAndUpdateEmployeeData([employeeData]);

            // Check if the update was successful and we have updated employee data
            if (result.success && result.updatedEmployees && result.updatedEmployees.length > 0) {
                const updatedEmployee = result.updatedEmployees[0];
                
                // IMPORTANT: Re-fetch the overtime data AFTER the update algorithm has run
                const { data: updatedOvertimeData } = await supabase
                    .from('overtime_vacation_days')
                    .select('days_remaining')
                    .eq('employee_id', updatedEmployee.id)
                    .single();
                
                // Fetch registered vacation days
                const { data: vacationDaysData } = await supabase
                    .from('vacation_days')
                    .select('vacation_date')
                    .eq('employee_id', updatedEmployee.id);

                // Ensure dates are parsed correctly
                const registeredDates = vacationDaysData.map(vacation => {
                    return new Date(vacation.vacation_date + 'T00:00:00'); // Set time to midnight to avoid timezone issues
                });
                
                // Update all state with the latest data
                setEmployee(updatedEmployee);
                setOvertimeDays(updatedOvertimeData?.days_remaining);
                setRegisteredVacationDays(registeredDates);
                setCurrentView('calendar');
            } else {
                setError('Error al actualizar los datos del empleado.');
            }
        } catch (error) {
            console.error('Error:', error);
            setError('Error al verificar el empleado. Por favor, intente nuevamente.');
        } finally {
            setLoading(false);
        }
    };

    // Function to check if the user has permission to manage the employee
    const checkUserPermission = (role, employeeArea) => {
        // Admin and RH can access all employees
        if (role === 'admin' || role === 'RH') {
            return true;
        }
        
        // CEO can only access certain areas
        if (role === 'CEO') {
            return ['Administrativo / Dirección', 'Comercial', 'Ventas'].includes(employeeArea);
        }
        
        // Other roles can only access employees in their own area
        if (['Fábrica ABAP', 'Staffing', 'PMO-IT', 'Finanzas'].includes(role)) {
            return employeeArea === role;
        }
        
        // Default deny
        return false;
    };

    const handleFinalizeVacation = async () => {
        setLoading(true);
        setError('');

        try {
            // Calculate the number of days to deduct based on mode
            const daysToDeduct = mode === 'full' ? selectedDates.length : 0.5;

            // Step 1: Update remaining_days and days_taken in employees table
            const newRemainingDays = employee.remaining_days - daysToDeduct;
            const newDaysTaken = (employee.days_taken || 0) + daysToDeduct;

            await supabase
                .from('employees')
                .update({
                    remaining_days: newRemainingDays,
                    days_taken: newDaysTaken
                })
                .eq('id', employee.id);

            // Step 2: Handle overtime_days logic
            const { data: overtimeData } = await supabase
                .from('overtime_vacation_days')
                .select('days_remaining')
                .eq('employee_id', employee.id)
                .single();

            if (overtimeData) {
                const daysRemaining = overtimeData.days_remaining;

                if (daysRemaining !== null) {
                    if (selectedDates.length >= daysRemaining) {
                        // Scenario 2: Selected days >= days_remaining
                        await supabase
                            .from('overtime_vacation_days')
                            .update({ days_remaining: null, expiration_date: null })
                            .eq('employee_id', employee.id);
                    } else {
                        // Scenario 3: Selected days < days_remaining
                        const newOvertimeDays = daysRemaining - selectedDates.length;
                        await supabase
                            .from('overtime_vacation_days')
                            .update({ days_remaining: newOvertimeDays })
                            .eq('employee_id', employee.id);
                    }
                }
            }

            // Step 3: Insert selected vacation days into vacation_days table
            const vacationDaysData = selectedDates.map(date => ({
                employee_id: employee.id,
                vacation_date: date.toISOString().split('T')[0],
                has_half_day: mode === 'half' // Set to true for half day mode
            }));

            await supabase
                .from('vacation_days')
                .insert(vacationDaysData);

            // Set confirmation message
            setConfirmationMessage(`Vacaciones registradas correctamente para ${employee.full_name}.`);
            setCurrentView('confirmation');
        } catch (error) {
            console.error('Error during vacation registration:', error);
            setError('Error al registrar las vacaciones. Por favor, intente nuevamente.');
        } finally {
            setLoading(false);
        }
    };

    const isWeekday = (date) => {
        const day = date.getDay();
        return day !== 0 && day !== 6;
    };

    const handleDateSelect = (date) => {
        if (mode === 'half') {
            // For half-day mode, only allow selecting one date
            setSelectedDates([date]);
        } else {
            // For full-day mode, toggle the date in the selection array
            setSelectedDates(prev => {
                const dateStr = date.toISOString().split('T')[0];
                if (prev.find(d => d.toISOString().split('T')[0] === dateStr)) {
                    return prev.filter(d => d.toISOString().split('T')[0] !== dateStr);
                }
                return [...prev, date];
            });
        }
    };

    const renderEmailView = () => (
        <div className="email-verification-container">
            <h2>Escribe el correo WSP del empleado al que se le darán vacaciones</h2>

            <div className="email-input-container">
                <input
                    type="text"
                    value={email}
                    onChange={handleEmailChange}
                    className="email-input"
                />
            </div>

            <div className="email-example">
                Ejemplo:   fernando.nunez@wsp.com.mx
            </div>

            <button
                className="submit-button"
                onClick={handleSubmit}
                disabled={loading}
            >
                {loading ? 'Verificando...' : 'Siguiente'}
            </button>

            {error && (
                <div className="error-message">
                    {error.split('\n').map((line, i) => (
                        <p key={i}>{line}</p>
                    ))}
                </div>
            )}
        </div>
    );

    const renderCalendarView = () => (
        <div className="vacation-selection-container">
            <h1>Se encontró al empleado {employee.full_name}, el cual cuenta con {employee.remaining_days} días disponibles.</h1>
            <p className="overtime-info">
                {overtimeDays !== null
                    ? `De los ${employee.remaining_days} días disponibles, ${overtimeDays} días son de prórroga.`
                    : 'Este empleado no cuenta con días de prórroga'}
            </p>
            <div className="vacation-selection-content">
                <div className="selection-counter">
                    <span className="counter-label">Has seleccionado</span>
                    <span className="counter-number">
                        {mode === 'half' ? '0.5' : selectedDates.length}
                    </span>
                    <span className="counter-label">
                        {mode === 'half' ? 'día en el calendario' : 'días en el calendario'}
                    </span>
                </div>

                <div className="calendar-container">
                    <h3>
                        {mode === 'half'
                            ? '¿Cuando se le dará el medio día?'
                            : '¿Qué días tomará el empleado?'}
                    </h3>
                    <DatePicker
                        inline
                        filterDate={date => isWeekday(date) && !registeredVacationDays.some(registeredDate => registeredDate.toDateString() === date.toDateString())}
                        selected={mode === 'half' && selectedDates.length > 0 ? selectedDates[0] : null}
                        onChange={handleDateSelect}
                        highlightDates={selectedDates}
                        multiple={mode === 'full'}
                    />
                </div>

                <div className="remaining-counter">
                    <span className="counter-label">Le quedan</span>
                    <span className="counter-number">
                        {employee.remaining_days - (mode === 'half' ? 0.5 : selectedDates.length)}
                    </span>
                    <span className="counter-label">días disponibles</span>
                </div>
            </div>

            <button
                className="submit-button"
                disabled={selectedDates.length === 0}
                onClick={handleFinalizeVacation}
            >
                Finalizar registro de vacaciones
            </button>
        </div>
    );

    const renderConfirmationView = () => (
        <div className="confirmation-container">
            <h1>{confirmationMessage}</h1>
            <p>Se han registrado los siguientes días:</p>
            <ul>
                {selectedDates.map(date => (
                    <li key={date.toISOString()}>
                        {mode === 'half' 
                            ? `Medio día del ${formatDate(date)}` 
                            : formatDate(date)
                        }
                    </li>
                ))}
            </ul>
            <button
                className="submit-button register-another-button"
                onClick={() => {
                    setCurrentView('email');
                    setSelectedDates([]);
                    setConfirmationMessage('');
                }}
            >
                Seleccionar otro empleado
            </button>
        </div>
    );

    const formatDate = (date) => {
        const options = { day: 'numeric', month: 'long', year: 'numeric' };
        return date.toLocaleDateString('es-MX', options).replace(',', ' del');
    };


    return (
        <div className="registrar-vacaciones">
            <Navbar />
            <div className="content">
                {currentView === 'email' ? renderEmailView() : currentView === 'calendar' ? renderCalendarView() : renderConfirmationView()}
            </div>
        </div>
    );
}

export default RegistrarVacaciones;